import React, { useState, useEffect, useMemo } from 'react';
import { useFarm } from '../contexts/FarmContext';
import { supabase } from '../config/supabase';
import OrganizationSelector from '../components/OrganizationSelector';
import FarmSelector from '../components/FarmSelector';
import {
  ArchiveBoxIcon,
  FunnelIcon,
  ScaleIcon,
  ShoppingCartIcon,
  TrashIcon,
  CubeIcon,
} from '@heroicons/react/24/outline';
import FarmSearchDropdown from '../components/FarmSearchDropdown';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

const Harvest = () => {
  const { farms, isAdmin, hasOrganizations, hasFarms } = useFarm();
  const [harvests, setHarvests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedFarmFilter, setSelectedFarmFilter] = useState('all');
  const [selectedProduce, setSelectedProduce] = useState('all');
  const [produces, setProduces] = useState([]);
  const [stats, setStats] = useState({
    produceTypes: 0,
    totalHarvest: 0,
    sold: 0,
    discarded: 0
  });
  const [dateRange, setDateRange] = useState({
    start: '2024-01-01',
    end: new Date(new Date().getFullYear(), 11, 30).toISOString().split('T')[0]
  });

  // Load produces for filter
  useEffect(() => {
    const loadProduces = async () => {
      const { data } = await supabase
        .from('produce_name')
        .select('id, name_produce')
        .order('name_produce');
      if (data) setProduces(data);
    };
    loadProduces();
  }, []);

  // Load harvest data
  useEffect(() => {
    const loadHarvests = async () => {
      setLoading(true);
      try {
        // Get yield transactions that are harvests (in = true means incoming harvest)
        let query = supabase
          .from('harvest_view')
          .select('*')
          .eq('yield_in', true)
          .order('yield_transaction_date', { ascending: false });

        // Apply date filter
        if (dateRange.start) {
          query = query.gte('yield_transaction_date', dateRange.start);
        }
        if (dateRange.end) {
          query = query.lte('yield_transaction_date', dateRange.end);
        }

        // Apply farm filter
        if (selectedFarmFilter !== 'all') {
          query = query.eq('farm_id', parseInt(selectedFarmFilter));
        }

        const { data, error } = await query.limit(200);

        if (error) {
          console.error('Error loading harvests:', error);
          setHarvests([]);
        } else {
          setHarvests(data || []);
          
          // Calculate stats
          const uniqueProduces = new Set(data?.map(h => h.produce_name).filter(Boolean) || []);
          const totalHarvest = data?.reduce((sum, h) => sum + (parseFloat(h.yield_transaction_amount) || 0), 0) || 0;
          
          // Sold = transactions where transaction_type indicates sale
          const sold = data?.filter(h => h.transaction_name?.toLowerCase().includes('sale') || h.transaction_name?.toLowerCase().includes('sold'))
            .reduce((sum, h) => sum + (parseFloat(h.yield_transaction_amount) || 0), 0) || 0;
          
          // Discarded = transactions where grade indicates discard or loss
          const discarded = data?.filter(h => h.grade_name?.toLowerCase().includes('reject') || h.grade_name?.toLowerCase().includes('discard'))
            .reduce((sum, h) => sum + (parseFloat(h.yield_transaction_amount) || 0), 0) || 0;
          
          setStats({
            produceTypes: uniqueProduces.size,
            totalHarvest,
            sold,
            discarded
          });
        }
      } catch (err) {
        console.error('Error:', err);
      } finally {
        setLoading(false);
      }
    };

    loadHarvests();
  }, [selectedFarmFilter, selectedProduce, dateRange]);

  // Process harvest data for chart - aggregate by month
  const chartData = useMemo(() => {
    if (!harvests.length) return [];
    
    // Group harvests by month
    const monthlyData = {};
    harvests.forEach(h => {
      if (!h.yield_transaction_date) return;
      const date = new Date(h.yield_transaction_date);
      const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      if (!monthlyData[monthKey]) {
        monthlyData[monthKey] = { month: monthKey, kg: 0 };
      }
      monthlyData[monthKey].kg += parseFloat(h.yield_transaction_amount) || 0;
    });
    
    // Convert to array and sort by date
    const sortedData = Object.values(monthlyData).sort((a, b) => a.month.localeCompare(b.month));
    
    // Format month labels
    return sortedData.map(d => ({
      ...d,
      monthLabel: new Date(d.month + '-01').toLocaleDateString('en-US', { month: 'short', year: 'numeric' }),
      kg: Math.round(d.kg * 100) / 100
    }));
  }, [harvests]);

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const formatWeight = (weight) => {
    if (!weight) return '0';
    return parseFloat(weight).toLocaleString(undefined, { maximumFractionDigits: 2 });
  };

  const getGradeBadge = (grade) => {
    if (!grade) return null;
    const gradeColors = {
      'a': 'bg-green-100 text-green-800',
      'b': 'bg-blue-100 text-blue-800',
      'c': 'bg-yellow-100 text-yellow-800',
      'reject': 'bg-red-100 text-red-800',
    };
    const colorClass = Object.entries(gradeColors).find(([key]) => 
      grade.toLowerCase().includes(key)
    )?.[1] || 'bg-gray-100 text-gray-800';
    
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${colorClass}`}>
        {grade}
      </span>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Harvest</h1>
          <p className="text-gray-600 text-sm sm:text-base">Track and manage harvest records across all farms</p>
        </div>
        <div className="flex items-center flex-wrap gap-2 sm:gap-3 lg:gap-4">
          {hasOrganizations && <OrganizationSelector />}
          {hasFarms && <FarmSelector />}
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
        <div className="flex items-center gap-2 mb-4">
          <FunnelIcon className="h-5 w-5 text-gray-500" />
          <h3 className="font-medium text-gray-900">Filters</h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {/* Date Range */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
            <input
              type="date"
              value={dateRange.start}
              onChange={(e) => setDateRange(prev => ({ ...prev, start: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
            <input
              type="date"
              value={dateRange.end}
              onChange={(e) => setDateRange(prev => ({ ...prev, end: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
            />
          </div>
          {/* Farm Filter */}
          <div>
            <FarmSearchDropdown
              farms={farms}
              selectedFarmId={selectedFarmFilter}
              onSelect={setSelectedFarmFilter}
              label="Farm"
            />
          </div>
          {/* Produce Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Produce</label>
            <select
              value={selectedProduce}
              onChange={(e) => setSelectedProduce(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
            >
              <option value="all">All Produces</option>
              {produces.map(p => (
                <option key={p.id} value={p.id}>
                  {p.name_produce}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Type of Produce</p>
              <p className="text-2xl font-bold text-gray-900">{stats.produceTypes}</p>
            </div>
            <div className="p-3 bg-purple-100 rounded-lg">
              <CubeIcon className="h-6 w-6 text-purple-600" />
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Total Harvest</p>
              <p className="text-2xl font-bold text-gray-900">{formatWeight(stats.totalHarvest)} <span className="text-sm font-normal text-gray-500">kg</span></p>
            </div>
            <div className="p-3 bg-green-100 rounded-lg">
              <ScaleIcon className="h-6 w-6 text-green-600" />
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Sold</p>
              <p className="text-2xl font-bold text-blue-600">{formatWeight(stats.sold)} <span className="text-sm font-normal text-gray-500">kg</span></p>
            </div>
            <div className="p-3 bg-blue-100 rounded-lg">
              <ShoppingCartIcon className="h-6 w-6 text-blue-600" />
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Discarded</p>
              <p className="text-2xl font-bold text-red-600">{formatWeight(stats.discarded)} <span className="text-sm font-normal text-gray-500">kg</span></p>
            </div>
            <div className="p-3 bg-red-100 rounded-lg">
              <TrashIcon className="h-6 w-6 text-red-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Harvest Chart */}
      {!loading && chartData.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Harvest Trend</h3>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={chartData} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="harvestGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#93c5fd" stopOpacity={0.8}/>
                    <stop offset="95%" stopColor="#93c5fd" stopOpacity={0.1}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis 
                  dataKey="monthLabel" 
                  tick={{ fontSize: 11, fill: '#6b7280' }}
                  tickLine={false}
                  axisLine={{ stroke: '#e5e7eb' }}
                  interval="preserveStartEnd"
                />
                <YAxis 
                  tick={{ fontSize: 11, fill: '#6b7280' }}
                  tickLine={false}
                  axisLine={{ stroke: '#e5e7eb' }}
                  tickFormatter={(value) => `${(value / 1000).toFixed(0)}k`}
                  label={{ value: 'kg', angle: -90, position: 'insideLeft', style: { textAnchor: 'middle', fill: '#6b7280', fontSize: 12 } }}
                />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: 'white', 
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                  }}
                  formatter={(value) => [`${value.toLocaleString()} kg`, 'Harvest']}
                  labelStyle={{ fontWeight: 600, color: '#374151' }}
                />
                <Area 
                  type="monotone" 
                  dataKey="kg" 
                  stroke="#60a5fa" 
                  strokeWidth={2}
                  fill="url(#harvestGradient)" 
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Harvest Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
            <span className="ml-3 text-gray-600">Loading harvest data...</span>
          </div>
        ) : harvests.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Farm</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Produce</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Grade</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Total Harvest</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {harvests.map((harvest) => (
                  <tr key={harvest.yield_transaction_id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatDate(harvest.yield_transaction_date)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {harvest.farm_name || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {harvest.produce_name || harvest.variety_name || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getGradeBadge(harvest.grade_name)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-right font-medium text-gray-900">
                      {formatWeight(harvest.yield_transaction_amount)} {harvest.unit_symbol || 'kg'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-12">
            <ArchiveBoxIcon className="h-12 w-12 mx-auto text-gray-400 mb-4" />
            <p className="text-gray-500">No harvest records found</p>
            <p className="text-sm text-gray-400 mt-1">Try adjusting your filters or date range</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Harvest;
